import React, { useEffect, useRef, useState, forwardRef, useImperativeHandle } from 'react';
import { getAdjuntosFac, subirAdjuntoFac, getAdjuntoFacBlob, borrarAdjuntoFac } from '../services/api';

// Soportes / adjuntos de una factura (soporte de recibido, remisión firmada, etc.).
// Se anexan automáticamente al correo cuando se envía la factura al cliente.
// Reutilizable: en el detalle de la factura y en un modal desde el listado.
// - titulo: muestra el encabezado "Soportes / Adjuntos".
// - subir:  muestra el botón "Subir soporte". Si es false, la subida se dispara
//           desde fuera con la ref: soportesRef.current.abrir().
const SoportesFactura = forwardRef(function SoportesFactura({ facturaId, titulo = true, subir = true }, ref) {
  const [lista, setLista] = useState([]);
  const [subiendo, setSubiendo] = useState(false);
  const [msg, setMsg] = useState('');
  const inputRef = useRef(null);

  const cargar = () => getAdjuntosFac(facturaId).then(r => setLista(r.data || [])).catch(() => {});
  useEffect(() => { cargar(); /* eslint-disable-next-line */ }, [facturaId]);

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const fd = new FormData();
    files.forEach(f => fd.append('archivos', f));
    setSubiendo(true); setMsg('');
    try { const r = await subirAdjuntoFac(facturaId, fd); setMsg(`✅ ${r.data.subidos} soporte(s) cargado(s)`); cargar(); }
    catch (err) { setMsg('❌ ' + (err.response?.data?.error || 'No se pudo subir')); }
    finally { setSubiendo(false); if (inputRef.current) inputRef.current.value = ''; setTimeout(() => setMsg(''), 4000); }
  };

  const descargar = async (a) => {
    try {
      const r = await getAdjuntoFacBlob(facturaId, a.id);
      const url = URL.createObjectURL(r.data);
      const link = document.createElement('a'); link.href = url; link.download = a.nombre; link.click();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
    } catch { setMsg('❌ No se pudo descargar'); }
  };

  const borrar = async (a) => {
    if (!window.confirm(`¿Eliminar el soporte "${a.nombre}"?`)) return;
    try { await borrarAdjuntoFac(facturaId, a.id); cargar(); } catch { setMsg('❌ No se pudo eliminar'); }
  };

  const fmtKB = (n) => n >= 1048576 ? (n / 1048576).toFixed(1) + ' MB' : Math.max(1, Math.round((n || 0) / 1024)) + ' KB';

  // Permite disparar la subida desde fuera (ej. icono del toolbar de la factura).
  useImperativeHandle(ref, () => ({ abrir: () => inputRef.current?.click() }));

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
        {titulo
          ? <h2 style={s.title}>📎 Soportes / Adjuntos {lista.length ? `(${lista.length})` : ''}</h2>
          : <span style={{ color: '#94a3b8', fontSize: 13 }}>{lista.length} soporte(s)</span>}
        {/* input siempre presente (para poder disparar la subida por ref); el botón es opcional */}
        <input ref={inputRef} type="file" multiple style={{ display: 'none' }} onChange={onFiles}
               accept=".pdf,.jpg,.jpeg,.png,.webp,.xlsx,.xls,.doc,.docx" />
        {subir && (
          <button style={{ ...s.btn, opacity: subiendo ? 0.7 : 1 }} disabled={subiendo} onClick={() => inputRef.current?.click()}>
            {subiendo ? 'Subiendo…' : '⬆ Subir soporte'}
          </button>
        )}
      </div>
      {msg && <div style={{ fontSize: 13, marginBottom: 10, color: msg.startsWith('✅') ? '#86efac' : '#fca5a5' }}>{msg}</div>}
      <div style={s.card}>
        {lista.length === 0 ? (
          <p style={{ color: '#475569', fontSize: 13, padding: '6px 2px', margin: 0 }}>Sin soportes. Sube el soporte de recibido para que se anexe al correo de la factura.</p>
        ) : lista.map(a => (
          <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 4px', borderBottom: '1px solid #1e293b' }}>
            <span style={{ fontSize: 16 }}>📄</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ color: '#e2e8f0', fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.nombre}</div>
              <div style={{ color: '#64748b', fontSize: 11 }}>{fmtKB(a.tamano)} · {new Date(a.created_at).toLocaleDateString('es-CO')}</div>
            </div>
            <button style={s.link} onClick={() => descargar(a)}>Descargar</button>
            <button style={s.linkDel} onClick={() => borrar(a)}>Eliminar</button>
          </div>
        ))}
      </div>
      {lista.length > 0 && <p style={{ color: '#64748b', fontSize: 12, marginTop: 8 }}>ℹ️ Estos soportes se anexan automáticamente al correo cuando envías la factura al cliente.</p>}
    </div>
  );
});

export default SoportesFactura;

const s = {
  title: { color: '#f1f5f9', fontSize: 15, fontWeight: 600, margin: 0 },
  btn: { background: '#1d4ed8', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  card: { background: '#0f172a', border: '1px solid #1e293b', borderRadius: 10, padding: '6px 12px' },
  link: { background: '#1e293b', border: '1px solid #334155', color: '#93c5fd', borderRadius: 6, padding: '4px 10px', fontSize: 12, cursor: 'pointer' },
  linkDel: { background: '#1e293b', border: '1px solid #7f1d1d', color: '#fca5a5', borderRadius: 6, padding: '4px 10px', fontSize: 12, cursor: 'pointer' },
};
